import {
  EulerAngles,
  OneDOFAngles,
  Point3D,
  PoseAngles,
  ThreeDOFJoints,
  TwoDOFAngles,
  TwoDOFJoints,
  Vector3D,
} from "/@/shared/motion-menu-system/MenuTypes";

// ===== HELPER FUNCTIONS (defined once, outside main function) =====

// Calculate vector from point A to point B
function vector(a: Point3D, b: Point3D): Vector3D {
  return {
    x: b.x - a.x,
    y: b.y - a.y,
    z: b.z - a.z,
  };
}

// Normalize a vector
function normalize(v: Vector3D): Vector3D {
  const mag = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
  if (mag === 0) return { x: 0, y: 0, z: 0 };
  return {
    x: v.x / mag,
    y: v.y / mag,
    z: v.z / mag,
  };
}

// Dot product
function dot(v1: Vector3D, v2: Vector3D): number {
  return v1.x * v2.x + v1.y * v2.y + v1.z * v2.z;
}

// Cross product
function cross(v1: Vector3D, v2: Vector3D): Vector3D {
  return {
    x: v1.y * v2.z - v1.z * v2.y,
    y: v1.z * v2.x - v1.x * v2.z,
    z: v1.x * v2.y - v1.y * v2.x,
  };
}

// Calculate hinge angle (1 DOF) - returns angle in radians
function calculateHingeAngle(
  pointA: Point3D,
  pointB: Point3D,
  pointC: Point3D,
): number {
  const ba = normalize(vector(pointB, pointA));
  const bc = normalize(vector(pointB, pointC));
  const cosAngle = Math.max(-1, Math.min(1, dot(ba, bc)));
  return Math.acos(cosAngle);
}

// Calculate Euler angles (3 DOF) for ball-and-socket joints
// Returns {pitch, yaw, roll} in radians
function calculateEulerAngles(
  joint: Point3D,
  distal: Point3D,
  proximal: Point3D,
  midlineRef: Point3D,
): EulerAngles {
  // Create local coordinate system at the joint
  // Z-axis: from joint to distal point
  const zAxis = normalize(vector(joint, distal));

  // X-axis: perpendicular to Z and the reference direction
  const refVector = normalize(vector(joint, midlineRef));
  let xAxis = normalize(cross(refVector, zAxis));

  // Handle degenerate case
  if (
    Math.sqrt(xAxis.x * xAxis.x + xAxis.y * xAxis.y + xAxis.z * xAxis.z) < 0.01
  ) {
    xAxis = { x: 1, y: 0, z: 0 };
  }

  // Y-axis: perpendicular to both
  const yAxis = normalize(cross(zAxis, xAxis));

  // Pitch (up/down rotation around X-axis)
  const pitch = Math.asin(Math.max(-1, Math.min(1, -zAxis.y)));

  // Yaw (left/right rotation around Y-axis)
  const yaw = Math.atan2(zAxis.x, zAxis.z);

  // Roll (axial rotation) - using the proximal point to estimate twist
  const toProximal = normalize(vector(joint, proximal));
  const projectedOnPlane = {
    x: toProximal.x - dot(toProximal, zAxis) * zAxis.x,
    y: toProximal.y - dot(toProximal, zAxis) * zAxis.y,
    z: toProximal.z - dot(toProximal, zAxis) * zAxis.z,
  };
  const normalizedProj = normalize(projectedOnPlane);
  const roll = Math.atan2(
    dot(normalizedProj, yAxis),
    dot(normalizedProj, xAxis),
  );

  return { pitch, yaw, roll };
}

// Calculate 2 DOF angles (pitch and yaw, no roll)
// Used for joints with limited rotation like wrists and ankles
function calculate2DOFAngles(
  joint: Point3D,
  distal: Point3D,
  reference: Point3D,
): TwoDOFAngles {
  const toDistal = normalize(vector(joint, distal));

  // Pitch (up/down)
  const pitch = Math.asin(Math.max(-1, Math.min(1, -toDistal.y)));

  // Yaw (left/right)
  const yaw = Math.atan2(toDistal.x, toDistal.z);

  return { pitch, yaw };
}

// ===== MAIN FUNCTION =====

/**
 * Calculates joint angles from MediaPipe Pose landmarks
 * @param {Array} landmarks - Array of 33 MediaPipe pose landmarks, each with {x, y, z, visibility}
 * @returns {Object} Object containing all joint angles organized by DOF
 */
export function calculatePoseAngles(
  landmarks: Point3D[],
): PoseAngles | undefined {
  if (!landmarks) {
    return;
  }
  // MediaPipe Pose landmark indices
  const POSE = {
    NOSE: 0,
    LEFT_EYE_INNER: 1,
    LEFT_EYE: 2,
    LEFT_EYE_OUTER: 3,
    RIGHT_EYE_INNER: 4,
    RIGHT_EYE: 5,
    RIGHT_EYE_OUTER: 6,
    LEFT_EAR: 7,
    RIGHT_EAR: 8,
    MOUTH_LEFT: 9,
    MOUTH_RIGHT: 10,
    LEFT_SHOULDER: 11,
    RIGHT_SHOULDER: 12,
    LEFT_ELBOW: 13,
    RIGHT_ELBOW: 14,
    LEFT_WRIST: 15,
    RIGHT_WRIST: 16,
    LEFT_PINKY: 17,
    RIGHT_PINKY: 18,
    LEFT_INDEX: 19,
    RIGHT_INDEX: 20,
    LEFT_THUMB: 21,
    RIGHT_THUMB: 22,
    LEFT_HIP: 23,
    RIGHT_HIP: 24,
    LEFT_KNEE: 25,
    RIGHT_KNEE: 26,
    LEFT_ANKLE: 27,
    RIGHT_ANKLE: 28,
    LEFT_HEEL: 29,
    RIGHT_HEEL: 30,
    LEFT_FOOT_INDEX: 31,
    RIGHT_FOOT_INDEX: 32,
  };

  // Calculate body midline reference points
  const leftShoulder = landmarks[POSE.LEFT_SHOULDER];
  const rightShoulder = landmarks[POSE.RIGHT_SHOULDER];
  const leftHip = landmarks[POSE.LEFT_HIP];
  const rightHip = landmarks[POSE.RIGHT_HIP];

  const shoulderMidpoint: Point3D = {
    x: (leftShoulder.x + rightShoulder.x) / 2,
    y: (leftShoulder.y + rightShoulder.y) / 2,
    z: (leftShoulder.z + rightShoulder.z) / 2,
  };

  const hipMidpoint: Point3D = {
    x: (leftHip.x + rightHip.x) / 2,
    y: (leftHip.y + rightHip.y) / 2,
    z: (leftHip.z + rightHip.z) / 2,
  };

  // Calculate head center
  const nose = landmarks[POSE.NOSE];
  const leftEar = landmarks[POSE.LEFT_EAR];
  const rightEar = landmarks[POSE.RIGHT_EAR];

  const headCenter: Point3D = {
    x: (leftEar.x + rightEar.x) / 2,
    y: (leftEar.y + rightEar.y) / 2,
    z: (leftEar.z + rightEar.z) / 2,
  };

  // Calculate hand centers (average of index, pinky, thumb for each hand)
  const leftHandCenter: Point3D = {
    x: (landmarks[POSE.LEFT_INDEX].x + landmarks[POSE.LEFT_PINKY].x +
      landmarks[POSE.LEFT_THUMB].x) / 3,
    y: (landmarks[POSE.LEFT_INDEX].y + landmarks[POSE.LEFT_PINKY].y +
      landmarks[POSE.LEFT_THUMB].y) / 3,
    z: (landmarks[POSE.LEFT_INDEX].z + landmarks[POSE.LEFT_PINKY].z +
      landmarks[POSE.LEFT_THUMB].z) / 3,
  };

  const rightHandCenter: Point3D = {
    x: (landmarks[POSE.RIGHT_INDEX].x + landmarks[POSE.RIGHT_PINKY].x +
      landmarks[POSE.RIGHT_THUMB].x) / 3,
    y: (landmarks[POSE.RIGHT_INDEX].y + landmarks[POSE.RIGHT_PINKY].y +
      landmarks[POSE.RIGHT_THUMB].y) / 3,
    z: (landmarks[POSE.RIGHT_INDEX].z + landmarks[POSE.RIGHT_PINKY].z +
      landmarks[POSE.RIGHT_THUMB].z) / 3,
  };

  // ===== 1 DOF JOINTS =====
  const oneDOF: OneDOFAngles = {
    // Elbows - hinge joints
    elbow_L_flexion: calculateHingeAngle(
      landmarks[POSE.LEFT_SHOULDER],
      landmarks[POSE.LEFT_ELBOW],
      landmarks[POSE.LEFT_WRIST],
    ),
    elbow_R_flexion: calculateHingeAngle(
      landmarks[POSE.RIGHT_SHOULDER],
      landmarks[POSE.RIGHT_ELBOW],
      landmarks[POSE.RIGHT_WRIST],
    ),

    // Knees - hinge joints
    knee_L_flexion: calculateHingeAngle(
      landmarks[POSE.LEFT_HIP],
      landmarks[POSE.LEFT_KNEE],
      landmarks[POSE.LEFT_ANKLE],
    ),
    knee_R_flexion: calculateHingeAngle(
      landmarks[POSE.RIGHT_HIP],
      landmarks[POSE.RIGHT_KNEE],
      landmarks[POSE.RIGHT_ANKLE],
    ),
  };

  // ===== 2 DOF JOINTS =====

  // Wrists - flexion/extension and radial/ulnar deviation
  const wrist_L = calculate2DOFAngles(
    landmarks[POSE.LEFT_WRIST],
    leftHandCenter,
    landmarks[POSE.LEFT_ELBOW],
  );

  const wrist_R = calculate2DOFAngles(
    landmarks[POSE.RIGHT_WRIST],
    rightHandCenter,
    landmarks[POSE.RIGHT_ELBOW],
  );

  // Ankles - dorsiflexion/plantarflexion and inversion/eversion
  const leftFootCenter: Point3D = {
    x: (landmarks[POSE.LEFT_HEEL].x + landmarks[POSE.LEFT_FOOT_INDEX].x) / 2,
    y: (landmarks[POSE.LEFT_HEEL].y + landmarks[POSE.LEFT_FOOT_INDEX].y) / 2,
    z: (landmarks[POSE.LEFT_HEEL].z + landmarks[POSE.LEFT_FOOT_INDEX].z) / 2,
  };

  const rightFootCenter: Point3D = {
    x: (landmarks[POSE.RIGHT_HEEL].x + landmarks[POSE.RIGHT_FOOT_INDEX].x) / 2,
    y: (landmarks[POSE.RIGHT_HEEL].y + landmarks[POSE.RIGHT_FOOT_INDEX].y) / 2,
    z: (landmarks[POSE.RIGHT_HEEL].z + landmarks[POSE.RIGHT_FOOT_INDEX].z) / 2,
  };

  const ankle_L = calculate2DOFAngles(
    landmarks[POSE.LEFT_ANKLE],
    leftFootCenter,
    landmarks[POSE.LEFT_KNEE],
  );

  const ankle_R = calculate2DOFAngles(
    landmarks[POSE.RIGHT_ANKLE],
    rightFootCenter,
    landmarks[POSE.RIGHT_KNEE],
  );

  const twoDOF: TwoDOFJoints = {
    wrist_L_pitch: wrist_L.pitch, // flexion/extension
    wrist_L_yaw: wrist_L.yaw, // radial/ulnar deviation

    wrist_R_pitch: wrist_R.pitch,
    wrist_R_yaw: wrist_R.yaw,

    ankle_L_pitch: ankle_L.pitch, // dorsiflexion/plantarflexion
    ankle_L_yaw: ankle_L.yaw, // inversion/eversion

    ankle_R_pitch: ankle_R.pitch,
    ankle_R_yaw: ankle_R.yaw,
  };

  // ===== 3 DOF JOINTS =====

  // Shoulders - ball and socket joints
  const shoulder_L = calculateEulerAngles(
    landmarks[POSE.LEFT_SHOULDER],
    landmarks[POSE.LEFT_ELBOW],
    landmarks[POSE.LEFT_WRIST],
    hipMidpoint,
  );

  const shoulder_R = calculateEulerAngles(
    landmarks[POSE.RIGHT_SHOULDER],
    landmarks[POSE.RIGHT_ELBOW],
    landmarks[POSE.RIGHT_WRIST],
    hipMidpoint,
  );

  // Hips - ball and socket joints
  const hip_L = calculateEulerAngles(
    landmarks[POSE.LEFT_HIP],
    landmarks[POSE.LEFT_KNEE],
    landmarks[POSE.LEFT_ANKLE],
    shoulderMidpoint,
  );

  const hip_R = calculateEulerAngles(
    landmarks[POSE.RIGHT_HIP],
    landmarks[POSE.RIGHT_KNEE],
    landmarks[POSE.RIGHT_ANKLE],
    shoulderMidpoint,
  );

  // Neck - 3 DOF
  const neck = calculateEulerAngles(
    shoulderMidpoint,
    headCenter,
    nose,
    hipMidpoint,
  );

  // Torso/Spine orientation
  const torsoVector = normalize(vector(hipMidpoint, shoulderMidpoint));
  const torso: EulerAngles = {
    pitch: Math.asin(Math.max(-1, Math.min(1, -torsoVector.y))),
    yaw: Math.atan2(torsoVector.x, torsoVector.z),
    roll: Math.atan2(
      rightShoulder.y - leftShoulder.y,
      rightShoulder.x - leftShoulder.x,
    ),
  };

  const threeDOF: ThreeDOFJoints = {
    shoulder_L_pitch: shoulder_L.pitch,
    shoulder_L_yaw: shoulder_L.yaw,
    shoulder_L_roll: shoulder_L.roll,

    shoulder_R_pitch: shoulder_R.pitch,
    shoulder_R_yaw: shoulder_R.yaw,
    shoulder_R_roll: shoulder_R.roll,

    hip_L_pitch: hip_L.pitch,
    hip_L_yaw: hip_L.yaw,
    hip_L_roll: hip_L.roll,

    hip_R_pitch: hip_R.pitch,
    hip_R_yaw: hip_R.yaw,
    hip_R_roll: hip_R.roll,

    neck_pitch: neck.pitch,
    neck_yaw: neck.yaw,
    neck_roll: neck.roll,

    spine_pitch: torso.pitch,
    spine_yaw: torso.yaw,
    spine_roll: torso.roll,
  };

  // Return organized by degrees of freedom
  return {
    oneDOF, // 4 angles (elbows, knees)
    twoDOF, // 8 angles (wrists, ankles = 4 joints × 2)
    threeDOF, // 18 angles (shoulders, hips, neck, spine = 6 joints × 3)
  };
  // Total: 30 angles for full pose with wrists and ankles
}

// Example usage:
// const angles = calculatePoseAngles(mediaPipePoseLandmarks);
// console.log(angles.oneDOF.elbow_L_flexion);
// console.log(angles.twoDOF.wrist_L_pitch);     // Wrist flexion/extension
// console.log(angles.twoDOF.ankle_R_pitch);     // Ankle up/down
// console.log(angles.threeDOF.shoulder_L_pitch);
